// The "How this range is built" row set — one definition, both renderers.
//
// The SPA (src/Results.tsx) and the shareable report page (functions/r/[id].ts)
// each used to build this list independently. They drifted: the two hour
// formats diverged, and when a pricing bucket was added neither picked it up,
// so both printed a breakdown that summed $20-$75 short of its own headline.
// The row SET lives here; each renderer keeps its own money/hours formatting,
// which is the part that legitimately differs between React and raw HTML.

import type { Band, EstimateTotals, PricingBasis } from "./types";

export interface BreakdownRow {
  /** Stable identity for React keys and tests; not shown to the user. */
  key: string;
  label: string;
  amount: { low: number; high: number };
  /** Labor hours behind the row, for the renderer to format. */
  hours?: Band;
  /** Non-hours annotation, e.g. "aftermarket pricing". */
  meta?: string;
}

/**
 * Breakdown rows in print order, already filtered to those worth showing.
 *
 * Every bucket in EstimateTotals except `notIncludedOperations` (which overlaps
 * the others by design) and `total` appears here exactly once, so the rows
 * always sum to the headline. `shopSupplies` is optional: reports stored before
 * 2026.09.2 have no such key and must still render.
 */
export function breakdownRows(totals: EstimateTotals, basis?: PricingBasis): BreakdownRow[] {
  const hrs = (t: keyof NonNullable<PricingBasis["laborHours"]>) => basis?.laborHours?.[t];

  const rows: BreakdownRow[] = [
    { key: "parts", label: "Parts", amount: totals.parts, meta: basis ? `${basis.tier} pricing` : undefined },
    { key: "bodyLabor", label: "Body labor", amount: totals.bodyLabor, hours: hrs("body") },
    { key: "refinishLabor", label: "Refinish labor", amount: totals.refinishLabor, hours: hrs("refinish") },
    { key: "mechanicalLabor", label: "Mechanical labor", amount: totals.mechanicalLabor, hours: hrs("mechanical") },
    { key: "frameLabor", label: "Frame / structural", amount: totals.frameLabor, hours: hrs("frame") },
    { key: "glassLabor", label: "Glass", amount: totals.glassLabor, hours: hrs("glass") },
    { key: "paintMaterials", label: "Paint and materials", amount: totals.paintMaterials },
    { key: "shopSupplies", label: "Shop supplies", amount: totals.shopSupplies ?? { low: 0, high: 0 } },
    { key: "sublet", label: "Sublet", amount: totals.sublet },
  ];

  return rows.filter((r) => r.amount.high > 0);
}
